import { cross, equals, negate, type Vec3 } from './vec.js';

/** The six 90° rotations a player can make, all relative to their own orientation. */
export type Rotation = 'turnLeft' | 'turnRight' | 'pitchUp' | 'pitchDown' | 'rollLeft' | 'rollRight';

export const ROTATIONS: readonly Rotation[] = [
  'turnLeft',
  'turnRight',
  'pitchUp',
  'pitchDown',
  'rollLeft',
  'rollRight',
];

/**
 * There is no world "up": orientation is the direction the player faces plus the
 * direction their head points. Always two perpendicular unit axes (24 possible states).
 */
export interface Orientation {
  readonly facing: Vec3;
  readonly up: Vec3;
}

export const DEFAULT_ORIENTATION: Orientation = { facing: [0, 0, -1], up: [0, 1, 0] };

/** The player's right-hand direction. */
export function rightOf(o: Orientation): Vec3 {
  return cross(o.facing, o.up);
}

export function rotate(o: Orientation, rotation: Rotation): Orientation {
  const r = rightOf(o);
  switch (rotation) {
    case 'turnLeft':
      return { facing: negate(r), up: o.up };
    case 'turnRight':
      return { facing: r, up: o.up };
    case 'pitchUp':
      return { facing: o.up, up: negate(o.facing) };
    case 'pitchDown':
      return { facing: negate(o.up), up: o.facing };
    case 'rollLeft':
      return { facing: o.facing, up: negate(r) };
    case 'rollRight':
      return { facing: o.facing, up: r };
  }
}

export function sameOrientation(a: Orientation, b: Orientation): boolean {
  return equals(a.facing, b.facing) && equals(a.up, b.up);
}

export function orientationKey(o: Orientation): string {
  return `${o.facing.join(',')}|${o.up.join(',')}`;
}

/**
 * The next action that brings `dir` into view: 'step' when already facing it,
 * otherwise the single rotation that faces it (behind needs two turns, so the first turn is returned).
 */
export function nextActionToward(o: Orientation, dir: Vec3): Rotation | 'step' {
  if (equals(dir, o.facing)) return 'step';
  const r = rightOf(o);
  if (equals(dir, r)) return 'turnRight';
  if (equals(dir, negate(r))) return 'turnLeft';
  if (equals(dir, o.up)) return 'pitchUp';
  if (equals(dir, negate(o.up))) return 'pitchDown';
  return 'turnLeft';
}
